import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Star, Send, Loader2, MessageSquare, User } from 'lucide-react';

// Star Rating Component for testimonials
const StarRating = ({ rating, setRating, size = 'lg' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="transition-all duration-150 cursor-pointer hover:scale-110"
        >
          <Star
            className={`${sizeClasses[size]} ${
              (hoverRating || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-300'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const TestimonialForm = ({ 
  user, 
  transactionId = null, 
  triggerText = "Laissez un témoignage",
  triggerVariant = "default",
  triggerClassName = "",
  onSuccess = null
}) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [displayName, setDisplayName] = useState(user?.name?.split(' ')[0] || '');
  const [role, setRole] = useState('buyer'); // buyer or seller
  const [submitting, setSubmitting] = useState(false);
  const [allowPublic, setAllowPublic] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    
    if (!comment.trim()) {
      toast.error('Veuillez écrire un commentaire');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        rating,
        comment: comment.trim(),
        display_name: displayName.trim() || 'Anonyme',
        role,
        allow_public: allowPublic,
        transaction_id: transactionId
      };

      await axios.post(`${API}/testimonials`, payload);
      
      toast.success('Merci pour votre témoignage !');
      setOpen(false);
      
      // Reset form
      setRating(0);
      setComment('');
      setAllowPublic(true);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du témoignage');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={triggerVariant}
          className={`btn-animated btn-glow ${triggerClassName}`}
          data-testid="testimonial-trigger-btn"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-400" />
            Partagez votre expérience
          </DialogTitle>
          <DialogDescription>
            Votre avis aide d'autres utilisateurs à faire confiance à Middleman.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Rating */}
          <div className="space-y-2">
            <Label>Note globale *</Label>
            <div className="flex justify-center py-2">
              <StarRating rating={rating} setRating={setRating} />
            </div>
            {rating > 0 && (
              <p className="text-center text-sm text-gray-500">
                {rating === 5 && 'Excellent !'}
                {rating === 4 && 'Très bien'}
                {rating === 3 && 'Bien'}
                {rating === 2 && 'Correct'}
                {rating === 1 && 'Décevant'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">Votre témoignage *</Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Racontez votre expérience avec Middleman..."
              className="min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 text-right">{comment.length}/500</p>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Prénom affiché</Label>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" />
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Prénom ou pseudo"
                maxLength={30}
              />
            </div>
            <p className="text-xs text-gray-500">Seul le prénom + première lettre du nom seront affichés (ex: Marc L.)</p>
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label>Vous étiez</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={role === 'buyer' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRole('buyer')}
                className={role === 'buyer' ? 'bg-[#67ffbb] text-gray-900 hover:bg-[#4de6a8]' : ''}
              >
                Acheteur
              </Button>
              <Button
                type="button"
                variant={role === 'seller' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRole('seller')}
                className={role === 'seller' ? 'bg-[#67ffbb] text-gray-900 hover:bg-[#4de6a8]' : ''}
              >
                Vendeur
              </Button>
            </div>
          </div>

          {/* Allow Public */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowPublic"
              checked={allowPublic}
              onChange={(e) => setAllowPublic(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="allowPublic" className="text-sm cursor-pointer">
              Autoriser l'affichage de mon témoignage sur le site
            </Label>
          </div>
        </form>

        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || rating === 0 || !comment.trim()}
            className="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 btn-animated"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Envoyer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestimonialForm;
